// Réutilise le même bundle Plotly que react-plotly.js (évite un second build dans le bundle).
import Plotly from 'plotly.js/dist/plotly';
import { ICON_PATHS } from './iconSvgPaths';

// Export PNG « complet » : compose l'image du graphe Plotly (toImage) avec les
// overlays React (icônes SVG des catégories) et la légende custom, sur un canvas.
// Aucune dépendance externe : tout est dessiné via l'API Canvas 2D.

const LEGEND_W = 218;
const LEGEND_ITEM_H = 24;
const LEGEND_TOP = 60;
const LEGEND_PAD = 6;

// Dessine un ou plusieurs paths SVG (d'un ICON_PATHS) centré en (cx, cy) à `size` px.
function drawSvgIcon(ctx, entry, color, cx, cy, size) {
  const [vbX, vbY, vbW, vbH] = entry.viewBox.split(/[\s,]+/).map(Number);
  ctx.save();
  ctx.translate(cx - size / 2, cy - size / 2);
  ctx.scale(size / vbW, size / vbH);
  ctx.translate(-vbX, -vbY);
  ctx.fillStyle = color;
  entry.d.forEach((d) => ctx.fill(new Path2D(d)));
  ctx.restore();
}

// Tronque un texte avec « … » s'il dépasse maxW pixels.
function truncate(ctx, text, maxW) {
  if (ctx.measureText(text).width <= maxW) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(t + '…').width > maxW) t = t.slice(0, -1);
  return t + '…';
}

function dashPattern(dash) {
  switch (dash) {
    case 'dot': return [3, 3];
    case 'dash': return [8, 4];
    case 'dashdot': return [8, 4, 3, 4];
    case 'longdash': return [16, 4];
    default: return [];
  }
}

// Dessine un seul item de légende (entouré d'un try/catch pour ne pas bloquer les suivants).
function drawLegendItem(ctx, item, ix, cy) {
  if (item.type === 'signal' || item.type === 'alarm') {
    ctx.strokeStyle = item.color || '#333';
    ctx.lineWidth = 2;
    ctx.setLineDash(dashPattern(item.dash));
    ctx.beginPath();
    ctx.moveTo(ix, cy);
    ctx.lineTo(ix + 24, cy);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = '#1a1a2e';
    ctx.fillText(truncate(ctx, item.name ?? '', LEGEND_W - 46), ix + 30, cy);
    return;
  }

  const entry = item.type === 'category' ? ICON_PATHS[item.iconType] : null;
  if (entry) {
    drawSvgIcon(ctx, entry, item.color || '#888', ix + 7, cy, 16);
  } else {
    ctx.fillStyle = item.color || '#888';
    ctx.beginPath();
    ctx.arc(ix + 6, cy, 6, 0, 2 * Math.PI);
    ctx.fill();
  }
  ctx.fillStyle = item.type === 'category' || item.type === 'label' ? (item.color || '#1a1a2e') : '#1a1a2e';
  ctx.fillText(truncate(ctx, item.name ?? '', LEGEND_W - 40), ix + 22, cy);
}

// Reproduit la légende custom React sur le canvas, en haut à droite du graphe.
function drawLegend(ctx, items, plotW, plotH) {
  if (!items?.length) return;
  // Hauteur d'item adaptée pour que tous les items tiennent dans la hauteur du graphe.
  const maxH = plotH - LEGEND_TOP - 10;
  const itemH = Math.min(LEGEND_ITEM_H, Math.max(14, (maxH - LEGEND_PAD * 2) / items.length));
  const x0 = plotW - LEGEND_W;
  const y0 = LEGEND_TOP;
  const h = items.length * itemH + LEGEND_PAD * 2;

  ctx.fillStyle = 'rgba(255,255,255,0.97)';
  ctx.strokeStyle = '#d0d5e8';
  ctx.lineWidth = 1;
  ctx.fillRect(x0, y0, LEGEND_W, h);
  ctx.strokeRect(x0, y0, LEGEND_W, h);

  ctx.font = `${Math.min(14, itemH - 8)}px Montserrat, Arial, sans-serif`;
  ctx.textBaseline = 'middle';

  items.forEach((item, i) => {
    const cy = y0 + LEGEND_PAD + i * itemH + itemH / 2;
    const ix = x0 + 12;
    try {
      drawLegendItem(ctx, item, ix, cy);
    } catch (e) {
      // Un item défaillant ne doit pas empêcher de dessiner les autres.
      // eslint-disable-next-line no-console
      console.warn('[exportGraphImage] legend item skipped:', item, e);
    }
  });
}

/**
 * Compose et télécharge le PNG complet du graphe label.
 * @param {HTMLElement} gd        — div Plotly (.js-plotly-plot)
 * @param {Array} iconData        — descripteurs SVG en coordonnées données :
 *                                  [{ xDate, markerY, color, iconType, legendGroup }]
 *                                  Les positions px sont RECALCULÉES ici depuis gd._fullLayout
 *                                  pour rester alignées avec l'image (y compris après zoom).
 * @param {Set} hiddenGroups      — legendgroups masqués (icônes non dessinées)
 * @param {Array} legendItems     — items de la légende custom
 * @param {number} iconSize       — taille px des icônes overlay (défaut 22)
 * @param {string} fileName       — nom du fichier (sans extension)
 */
export async function exportGraphImage({ gd, iconData = [], hiddenGroups, legendItems = [], iconSize = 22, fileName = 'graphe' }) {
  if (!gd) return;
  const fl = gd._fullLayout || {};
  const width = fl.width || gd.clientWidth || 1000;
  const height = fl.height || gd.clientHeight || 600;
  const scale = 2;

  // S'assure que la police Montserrat est chargée avant de dessiner les textes.
  if (document.fonts?.ready) {
    try { await document.fonts.ready; } catch { /* ignore */ }
  }

  // 1. Image du graphe Plotly seul
  const chartUrl = await Plotly.toImage(gd, { format: 'png', width, height, scale });

  // 2. Canvas de composition
  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, width, height);

  const chartImg = new Image();
  await new Promise((res, rej) => {
    chartImg.onload = res;
    chartImg.onerror = rej;
    chartImg.src = chartUrl;
  });
  ctx.drawImage(chartImg, 0, 0, width, height);

  // 3. Icônes SVG des catégories — positions RECALCULÉES depuis le layout courant du graphe
  // (xaxis.d2p / yaxis2.l2p), exactement comme l'image Plotly → alignées même après zoom.
  const xa = fl.xaxis;
  const y2 = fl.yaxis2;
  const margin = fl.margin || { l: 0, t: 0 };
  if (xa?.d2p && y2?.l2p) {
    iconData.forEach(({ xDate, markerY, color, iconType, legendGroup }) => {
      if (hiddenGroups?.has?.(legendGroup)) return;
      const entry = ICON_PATHS[iconType];
      if (!entry) return;
      const xPx = margin.l + xa.d2p(xDate);
      const yPx = margin.t + y2.l2p(markerY);
      // Ignore les icônes hors de la zone de tracé (ex. zoom les place hors champ)
      if (xPx < margin.l || xPx > width - (margin.r || 0)) return;
      drawSvgIcon(ctx, entry, color, xPx, yPx, iconSize);
    });
  }

  // 4. Légende custom
  drawLegend(ctx, legendItems, width, height);

  // 5. Téléchargement
  const a = document.createElement('a');
  a.download = `${fileName}.png`;
  a.href = canvas.toDataURL('image/png');
  a.click();
}
